import React, { Fragment, ReactNode, useState } from "react";
import { Form, Field } from 'react-final-form';
import TextField from './text_field';
import { Card, CardContent, Typography, Toolbar, Button, Dialog, DialogContent, DialogActions, LinearProgress } from '@mui/material';
import Config from '../config';
import EnhancedTable from './table';
import axios from 'axios';
import ExtendedField from './text_field';

interface Contact {
    CompanyName?: string;
    Company?: string;
    FirstName?: string;
    LastName?: string;
    Address1?: string;
    Address2?: string;
    City?: string;
    State?: string;
    Zip?: string;
    Country?: string;
    Phone?: string;
    Email?: string;
    [key: string]: any;
}

const updateAddress = ([name, contact]: [string, Contact], state: any, { changeValue }: any) => {
   changeValue(state, name, (value: any) => (contact));
}

const formatContact = (contact: Contact) => {
        let addr = "";
        if(contact.CompanyName){
            addr += contact.CompanyName + "\n";
        }
        if(contact.Company){
            addr += contact.Company + "\n";
        }
        if(contact.FirstName || contact.LastName){
            var firstLast = [contact.FirstName, contact.LastName].join(' ').trim();
            if(firstLast !== contact.CompanyName){
                addr += firstLast + "\n";
            }
        }
        if(contact.Address1){
            addr += contact.Address1 + "\n";
        }
        if(contact.Address2){
            addr += contact.Address2 + "\n";
        }
        if(contact.City || contact.State || contact.Zip){
            addr += (contact.City || '');
            if(contact.City && (contact.State || contact.Zip)){
                addr += ", ";
            }
            addr += (contact.State || '')
            if(contact.City || contact.State){
                addr += ' ';
            }
            addr += (contact.Zip || '') + "\n";
        }
        if(contact.Country){
            addr += contact.Country + "\n";
        }
        if(contact.Phone || contact.Email){
            if(contact.Phone){
                addr += "T: " + contact.Phone;
            }
            if(contact.Phone && contact.Email){
                addr += "  ";
            }
            if(contact.Email){
                addr += "E: " + contact.Email;
            }
            addr += '\n';
        }
        return addr;
    };

interface AddressSelectProps {
    mutators?: {
        updateAddress?: (name: string, contact: Contact) => void;
        [key: string]: any;
    };
    name?: string;
    [key: string]: any;
}

interface AddressSelectState {
    showAddresses: boolean;
    showConfirm: boolean;
    showEdit: boolean;
    disableConfirm: boolean;
    disableEdit?: boolean;
    address?: any;
    refresh?: boolean;
}

const AddressSelect = (props: AddressSelectProps) => {
    const [state, setStateRaw] = useState<AddressSelectState>({showAddresses: false, showConfirm: false, showEdit: false, disableConfirm: false});
    const setState = (partial: Partial<AddressSelectState>) => setStateRaw(prev => ({...prev, ...partial}));

    const showAddresses = async () => {
        setState({showAddresses: true});
    }

    const addressToContact = (address: any): Contact => {
        return {
            CompanyName: address.ShipToCompany,
            Address1: address.ShipToAddress1,
            Address2: address.ShipToAddress2,
            FirstName: address.ShipToFName,
            LastName: address.ShipToLName,
            City: address.ShipToCity,
            State: address.ShipToState,
            Zip: address.ShipToZip,
            Country: address.ShipToCountry,
            Phone: address.ShipToPhone
        };
    }

    const setAddress = (address: any) => {
        let contact = addressToContact(address);
        props.mutators.updateAddress(props.name, contact);
        setState({showAddresses: false});
    }

    const deleteAddress = async () => {
        setState({disableConfirm: true});
        await axios.delete(Config.api + `/odata/Company/Addresses(${state.address.Id})`);
        setStateRaw(prev => ({...prev, showConfirm: false, refresh: !prev.refresh}));
    }

    const updateAddress = async (values: any) => {
        setState({disableEdit: true});
        if(values.Id){
            await axios.put(Config.api + `/odata/Company/Addresses(${state.address.Id})`, values);
        }else{
            await axios.post(Config.api + `/odata/Company/Addresses`, values);
        }
        setStateRaw(prev => ({...prev, showEdit: false, refresh: !prev.refresh}));
    }

    const loadAddress = async (id: any) => {
        setState({address: null, showEdit: true, disableEdit: false});
        var address = (await axios.get(Config.api + `/odata/Company/Addresses(${id})`)).data;
        setState({address: address});
    }

    let {mutators, ...rest} = props;
      let config = {
          url: Config.api + '/odata/Company/Addresses',
          columns: [
            { id: "Id", hidden: true},
            { id: 'AddressName', filterable: true, label: 'Name', width: '12em' },
            { id: 'ShipToAddress1', label: 'Address', template: (value, row) => <div style={{whiteSpace: 'pre-wrap', textAlign: 'left', paddingLeft: '10px', paddingTop: '10px', paddingBottom: '10px'}}>{formatContact(addressToContact(row))}</div> },
            { id: 'ShipToAddress2', hidden: true },
            { id: 'ShipToFName', hidden: true },
            { id: 'ShipToLName', hidden: true },
            { id: 'ShipToCity', hidden: true },
            { id: 'ShipToState', hidden: true },
            { id: 'ShipToZip', hidden: true },
            { id: 'ShipToCountry', hidden: true },
            { id: 'ShipToPhone', hidden: true },
            { id: 'ShipToEMail', hidden: true },
            { id: 'ShipToCompany', hidden: true },
            { command: 'commands', stopPropagation: 'true', width: '20em', template: (value, row) => <Fragment>
                <Button style={{marginRight: '1rem'}} variant='contained' size='small' color='primary' onClick={() => setAddress(row)}>Select</Button>
                <Button style={{marginRight: '1rem'}} variant='outlined' size='small' onClick={() => loadAddress(row.Id)}>Edit</Button>
                <Button variant='outlined' size='small' onClick={() => setState({address: row, showConfirm: true, disableConfirm: false})}>Delete</Button>
            </Fragment>}
        ],
        order: 'asc',
        orderBy: 'AddressName',
        keyField: 'Id',
        pageSize: 20,
        refresh: state.refresh,
        pageSizes: [10, 20, 50, 100],
        title: 'Address Book',
        actions: <Button variant='outlined' size='small' onClick={() => setState({address: {}, showEdit: true, disableEdit: false})}>New Address</Button>
      };

    return (
        <>
            <Button variant='outlined' size='small' {...rest} onClick={showAddresses}>Open Address Book</Button>
            <Dialog open={state.showAddresses} disableEnforceFocus={true} onClose={() => setState({showAddresses: false})} fullWidth {...({size: 'sm'} as any)}>
                {state.showAddresses ? <EnhancedTable wrapperStyle={{maxHeight: '400px'}} config={config}/> : <span/>}
            </Dialog>
            <Dialog
                maxWidth="sm"
                aria-labelledby="confirmation-dialog-title"
                open={state.showConfirm}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (state.disableConfirm) return;
                  }
                  setState({showConfirm: false});
                }}>
                <Toolbar className='lbtoolbar'>Confirm Delete</Toolbar>
                <DialogContent>
                  <Typography>Are you sure you want to delete this address?</Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setState({showConfirm: false})} disabled={state.disableConfirm} color="primary">
                    No
                  </Button>
                  <Button onClick={deleteAddress} disabled={state.disableConfirm} color="primary">
                    Yes
                  </Button>
                </DialogActions>
              </Dialog>
            <Dialog
                maxWidth="sm"
                open={state.showEdit}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (state.disableEdit) return;
                  }
                  setState({showEdit: false});
                }}
                fullWidth={true}>
                {state.address ?
                    <Form onSubmit={updateAddress}
                    initialValues={state.address}
                    render={({ handleSubmit, pristine, invalid, values }) => (
                      <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                      <Toolbar className='lbtoolbar'>Edit Address</Toolbar>
                    <DialogContent>
                    <div style={{display: 'flex', flexDirection: 'column', flexShrink: '0'}}>
                      <Field label='Address Name' component={ExtendedField as any} name='AddressName' validate={v => !v ? 'Address name is required.' : undefined}/>
                      <Field label='Company Name' component={ExtendedField as any} name='ShipToCompany'/>
                      <div className='fieldHolder'>
                          <Field label='First Name' component={ExtendedField as any} name='ShipToFName'/>
                          <Field label='Last Name' component={ExtendedField as any} name='ShipToLName'/>
                      </div>
                      <Field label='Address Line 1' component={ExtendedField as any} name='ShipToAddress1'/>
                      <Field label='Address Line 2' component={ExtendedField as any} name='ShipToAddress2'/>
                      <div className='fieldHolder'>
                          <Field label='City' component={ExtendedField as any} name='ShipToCity'/>
                          <Field label='State' component={ExtendedField as any} name='ShipToState'/>
                          <Field label='ZIP Code' component={ExtendedField as any} name='ShipToZip'/>
                      </div>
                      <Field label='Country' component={ExtendedField as any} name='ShipToCountry'/>
                      <Field label='Phone' component={ExtendedField as any} name='ShipToPhone'/>
                      <Field label='Email' component={ExtendedField as any} name='ShipToEMail'/>
                      </div>
                    </DialogContent>
                    <DialogActions>
                      <Button type='submit' disabled={pristine || invalid || state.disableEdit} color="primary">
                        Save
                      </Button>
                    </DialogActions>
                    </form>
                    )}/>
                     :  <DialogContent><LinearProgress style={{height: '2em'}} variant="indeterminate"/></DialogContent> }

              </Dialog>
        </>
    );
}

interface AddressProps {
    name?: string;
    label?: ReactNode;
    contact?: Contact;
    disabled?: boolean;
    [key: string]: any;
}

const Address = (props: AddressProps) => {
      var {name, label, contact, ...rest} = props;
      let addr = formatContact(contact);

    return (
        <Card {...rest} className='address'>
            <Toolbar className='lbtoolbar'>{label}</Toolbar>
            <CardContent style={{display: 'flex', flexDirection: 'column'}}>
            {!props.disabled ?
                <div style={{display: 'flex', flexDirection: 'column'}}>
                <Field name={`${name}.CompanyName`} autoComplete='none' component={TextField as any} label='Company Name'/>
                <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Field name={`${name}.FirstName`} autoComplete='none' component={TextField as any} label='First Name' style={{flex: 1, marginRight: '1em'}}/>
                    <Field name={`${name}.LastName`} autoComplete='none' component={TextField as any} label='Last Name' style={{flex: 1}}/>
                </div>
                <Field name={`${name}.Address1`} autoComplete='none' component={TextField as any} label='Line 1'/>
                <Field name={`${name}.Address2`} autoComplete='none' component={TextField as any} label='Line 2'/>
                <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Field name={`${name}.City`} autoComplete='none' component={TextField as any} label='City' style={{flex: 1, marginRight: '1em'}}/>
                    <Field name={`${name}.State`} autoComplete='none' component={TextField as any} label='State' style={{flex: 1, marginRight: '1em'}}/>
                    <Field name={`${name}.Zip`} autoComplete='none' component={TextField as any} label='ZIP Code' style={{width: '5.5em'}}/>
                </div>
                <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Field name={`${name}.Country`} autoComplete='none' component={TextField as any} label='Country' style={{flex: 1, marginRight: '1em'}}/>
                    <Field name={`${name}.Phone`} autoComplete='none' component={TextField as any} label='Phone' style={{flex: 1, marginRight: '1em'}}/>
                    <Field name={`${name}.Email`} autoComplete='none' component={TextField as any} label='Email' style={{flex: 1}}/>
                </div>
                <div style={{paddingTop: '1em'}}>
                    <AddressSelect  {...props} variant='outlined' size='small'>Open Address Book</AddressSelect>
                </div>
                </div>
             :
             <Typography component='p' style={{whiteSpace: 'pre-wrap', textAlign: 'left'}}>
                {addr}
                </Typography>
            }
            </CardContent>
        </Card>
    );
}

export default Address;
export { updateAddress, formatContact, AddressSelect };
